import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import EntityConstants from "../../configs/EntityConstants";
import { POST, GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import { useTranslation } from "react-i18next";

export default function CreateBatchModal({
  isOpen,
  onClose,
  onBatchCreated,
  programmeCode,
  isEditMode = false,
  initialName = "",
  initialDescription = "",

  onUpdate,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    description: "",
  });
  const [programmeName, setProgrammeName] = useState("");
  const [batchName, setBatchName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const maxCharsBatchName = 250;
  const maxCharsBatchDescription = 250;

  useEffect(() => {
    if (isEditMode) {
      setName(initialName);
      setDescription(initialDescription);
    }
  }, [isEditMode, initialName, initialDescription]);

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      name: "",
      description: "",
    };

    if (!name.trim()) {
      newErrors.name = t("workspace:required_batch_name");
      valid = false;
    } else if (name.length > maxCharsBatchName) {
      newErrors.name = `${t(
        "workspace:batch_name_description_1",
      )} ${maxCharsBatchName} ${t("workspace:batch_name_description_2")}`;
      valid = false;
    }

    if (!description.trim()) {
      newErrors.description = t("workspace:required_description");
      valid = false;
    } else if (description.length > maxCharsBatchDescription) {
      newErrors.description = `${t(
        "workspace:description_description_1",
      )} ${maxCharsBatchDescription} ${t(
        "workspace:description_description_2",
      )}`;
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxCharsBatchName) {
      setName(value);
      setErrors({ ...errors, name: "" });
      setErrorMessage("");
    }
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxCharsBatchDescription) {
      setDescription(value);
      setErrors({ ...errors, description: "" });
      setErrorMessage("");
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setIsLoading(true);

    if (isEditMode) {
      onUpdate(name, description);
      setIsLoading(false);
      onClose();
      return;
    }

    const payload = {
      programme_code: programmeCode,
      batch_name: name,
      batch_description: description,
    };

    POST(EntityConstants.POST_CREATE_BATCH, payload)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          setErrorMessage(data);
          Toast({
            type: "error",
            title: t("workspace:error"),
            message: data.error || t("workspace:failed_create_batch"),
          });
        } else {
          Toast({
            type: "success",
            title: t("workspace:success"),
            message: t("workspace:success_create_batch"),
          });
          onBatchCreated();
          resetForm();
          onClose();
        }
      })
      .catch(() => {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:failed_create_batch"),
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setErrors({
      name: "",
      description: "",
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const fetchProgrammeName = () => {
    GET(EntityConstants.GET_PROGRAMME_NAME)
      .then((response) => response.json())
      .then((data) => {
        if (data.programme_display_name) {
          setProgrammeName(data.programme_display_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching program name:", error);
      });
  };

  const fetchBatchName = () => {
    GET(EntityConstants.GET_BATCH_NAME)
      .then((response) => response.json())
      .then((data) => {
        if (data.batch_display_name) {
          setBatchName(data.batch_display_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching batch name:", error);
      });
  };

  useEffect(() => {
    fetchProgrammeName();
    fetchBatchName();
  }, []);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id="create-batch-modal"
                className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-cfSecondary p-4 text-left align-middle shadow-xl transition-all"
              >
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="w-full">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-cfHeadingText"
                      >
                        {isEditMode
                          ? t("workspace:edit")
                          : t("workspace:create_new")}{" "}
                        {batchName ? batchName : t("workspace:batch")}
                      </Dialog.Title>
                      <p className="text-sm text-cfGlobalText mt-1">
                        {isEditMode
                          ? t("workspace:edit_the_text")
                          : t("workspace:create_new_text")}{" "}
                        {batchName ? batchName : t("workspace:batch")}{" "}
                        {isEditMode
                          ? `${t("workspace:details_description_1")} ${
                              programmeName ? programmeName : "Programme"
                            } ${t("workspace:details_description_3")}`
                          : `${t("workspace:details_description_2")} ${
                              programmeName ? programmeName : "Programme"
                            } ${t("workspace:details_description_3")}`}
                      </p>
                      <div className="space-y-4 mt-4">
                        <div>
                          <label
                            className="block mb-2 text-sm font-medium text-cfHeadingText after:content-['*'] after:text-red-500"
                            htmlFor="create-batch-modal-input-name"
                          >
                            {t("workspace:name")}
                          </label>
                          <input
                            type="text"
                            id="create-batch-modal-input-name"
                            className={`bg-cfTextInputBorder border ${
                              errors.name
                                ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                                : "border-cfIssuedScreenSearchBorder"
                            } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                            placeholder={`${t("workspace:enter")} ${
                              batchName ? batchName : t("workspace:batch")
                            } ${t("workspace:name")}`}
                            value={name}
                            onChange={handleNameChange}
                          />
                          <div className="flex justify-between items-center mt-1">
                            {errors.name && (
                              <p
                                id="create-batch-modal-error-name"
                                className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG"
                              >
                                {errors.name}
                              </p>
                            )}
                            <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                              <span>
                                {maxCharsBatchName - name.length}{" "}
                                {t("workspace:characters_left")}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label
                            className="block mb-2 text-sm font-medium text-cfHeadingText after:content-['*'] after:text-red-500"
                            htmlFor="create-batch-modal-input-description"
                          >
                            {t("workspace:description")}
                          </label>
                          <textarea
                            id="create-batch-modal-input-description"
                            rows="4"
                            className={`border ${
                              errors.description
                                ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                                : "border-cfIssuedScreenSearchBorder"
                            } text-cfGlobalText text-md rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full resize-none`}
                            placeholder={`${t("workspace:enter")} ${
                              batchName ? batchName : t("workspace:batch")
                            } ${t("workspace:description")}`}
                            value={description}
                            onChange={handleDescriptionChange}
                          ></textarea>
                          <div className="flex justify-between items-center mt-1">
                            {errors.description && (
                              <p
                                id="create-batch-modal-error-description"
                                className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG"
                              >
                                {errors.description}
                              </p>
                            )}
                            <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                              <span>
                                {maxCharsBatchDescription - description.length}{" "}
                                {t("workspace:characters_left")}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {errorMessage && (
                        <p
                          id="create-batch-modal-error-message"
                          className="mt-2 text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG flex items-center"
                        >
                          <span className="mr-1 text-base leading-none">*</span>
                          {errorMessage.error} - {errorMessage.message}
                        </p>
                      )}
                    </div>

                    <button
                      type="button"
                      id="create-batch-modal-close"
                      className="text-cfGlobalText hover:text-cfGlobalText focus:outline-none"
                      onClick={handleClose}
                    >
                      <span className="sr-only">Close</span>
                      &#10005;
                    </button>
                  </div>

                  <div className="flex mt-4 justify-end gap-4">
                    <button
                      type="button"
                      id="create-batch-modal-cancel"
                      className="px-4 py-2 text-cfHeadingText border hover:bg-cfCardHover transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2"
                      onClick={handleClose}
                      disabled={isLoading}
                    >
                      {t("workspace:cancel")}
                    </button>
                    <button
                      type="submit"
                      id="create-batch-modal-submit"
                      className="px-4 py-2 bg-cfButton hover:bg-cfPrimaryDark text-cfSecondary font-semibold border transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2 flex items-center justify-center min-w-[120px]"
                      disabled={isLoading}
                    >
                      {isLoading
                        ? isEditMode
                          ? t("workspace:updating")
                          : t("workspace:creating")
                        : `${
                            isEditMode
                              ? t("workspace:update")
                              : t("workspace:create")
                          } ${batchName ? batchName : t("workspace:batch")}`}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
